import React, { useEffect, useState, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Package, Tag, MessageSquare, Building2, Users, UtensilsCrossed, Percent, BarChart3, FileText, Settings, ShoppingCart, ClipboardList, UserCircle, MessageCircle, Truck, Briefcase } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  permission?: string;
  adminOnly?: boolean;
}
export function TopNav() {
  const {
    user
  } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 8);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };
  // Admin navigation items
  const adminNavItems: NavItem[] = [{
    path: '/admin/orders',
    label: 'Órdenes',
    icon: LayoutDashboard,
    permission: 'orders'
  }, {
    path: '/admin/production',
    label: 'Producción',
    icon: Package,
    permission: 'production'
  }, {
    path: '/admin/labels',
    label: 'Etiquetas',
    icon: Tag,
    permission: 'labels'
  }, {
    path: '/admin/assignments',
    label: 'Asignaciones',
    icon: Briefcase,
    permission: 'employees'
  }, {
    path: '/admin/claims',
    label: 'Reclamaciones',
    icon: MessageSquare,
    permission: 'claims'
  }, {
    path: '/admin/companies',
    label: 'Empresas',
    icon: Building2,
    permission: 'companies'
  }, {
    path: '/admin/employees',
    label: 'Empleados',
    icon: Users,
    permission: 'employees'
  }, {
    path: '/admin/menu',
    label: 'Menú',
    icon: UtensilsCrossed,
    permission: 'menu'
  }, {
    path: '/admin/promotions',
    label: 'Promociones',
    icon: Percent,
    permission: 'promotions'
  }, {
    path: '/admin/reports',
    label: 'Reportes',
    icon: BarChart3,
    permission: 'reports'
  }, {
    path: '/admin/activity-log',
    label: 'Registro',
    icon: FileText,
    permission: 'activityLog'
  }, {
    path: '/admin/settings',
    label: 'Configuración',
    icon: Settings,
    permission: 'settings'
  }];
  // Employee navigation items
  const employeeNavItems: NavItem[] = [{
    path: '/employee/order',
    label: 'Hacer Pedido',
    icon: ShoppingCart
  }, {
    path: '/employee/my-orders',
    label: 'Mis Pedidos',
    icon: ClipboardList
  }, ...(user?.permissions?.isManager ? [{
    path: '/employee/company-report',
    label: 'Consumos',
    icon: BarChart3
  }, {
    path: '/employee/employees',
    label: 'Empleados',
    icon: Users
  }] : []), {
    path: '/employee/profile',
    label: 'Mi Perfil',
    icon: UserCircle
  }];
  const navItems = user?.role === 'admin' ? adminNavItems : employeeNavItems;
  let filteredNavItems = navItems.filter(item => {
    if (user?.role === 'admin' && item.permission) {
      if (!user.adminPermissions) return true;
      return user.adminPermissions[item.permission as keyof typeof user.adminPermissions] === true;
    }
    return !item.adminOnly || user?.role === 'admin';
  });
  const isDeliveryUser = user?.role === 'admin' && ((user as any).assignedCompanies?.length > 0 || (user.adminPermissions as any)?.assignedCompanies?.length > 0);
  if (isDeliveryUser) {
    filteredNavItems = [{
      path: '/admin/delivery',
      label: 'Mis Entregas',
      icon: Truck
    }, ...filteredNavItems];
  }
  // useEffect AFTER filteredNavItems is defined to avoid ReferenceError
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      handleScroll();
    }, 100);
    el.addEventListener('scroll', handleScroll, {
      passive: true
    });
    window.addEventListener('resize', handleScroll, {
      passive: true
    });
    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [filteredNavItems.length]);
  return <nav className="hidden md:block bg-white border-b border-gray-100 shrink-0 z-30" data-id="element-1106">
      <div className="px-2 lg:px-8 relative" data-id="element-1107">
        {showRightFade && <button onClick={() => {
        const el = scrollRef.current;
        if (el) el.scrollBy({
          left: 120,
          behavior: 'smooth'
        });
      }} className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end pr-1.5" aria-label="Ver más opciones" data-id="element-1108">
            <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center shadow-sm" data-id="element-1109">
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" data-id="element-1110">
                <path d="M3.5 2L6.5 5L3.5 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1111" />
              </svg>
            </div>
          </button>}
        {showLeftFade && <button onClick={() => {
        const el = scrollRef.current;
        if (el) el.scrollBy({
          left: -120,
          behavior: 'smooth'
        });
      }} className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent z-10 flex items-center justify-start pl-1.5" aria-label="Ver opciones anteriores" data-id="element-1112">
            <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center shadow-sm" data-id="element-1113">
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" data-id="element-1114">
                <path d="M6.5 2L3.5 5L6.5 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1115" />
              </svg>
            </div>
          </button>}
        <div ref={scrollRef} className="flex items-center justify-start md:justify-center gap-0.5 overflow-x-auto scrollbar-hide w-full" style={{
        scrollbarWidth: 'none'
      } as React.CSSProperties} data-id="element-1116">
          {filteredNavItems.map(item => {
          const Icon = item.icon;
          return <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => `group relative flex items-center gap-1.5 px-3.5 py-3.5 transition-all duration-150 whitespace-nowrap flex-shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'}`} data-id="element-1117">
                {({
              isActive
            }) => <>
                    <Icon size={15} strokeWidth={isActive ? 2.5 : 2} className="shrink-0" data-id="element-1118" />
                    <span className={`text-xs md:text-sm ${isActive ? 'font-bold' : 'font-medium'}`} data-id="element-1119">
                      {item.label}
                    </span>
                    {/* Underline indicator */}
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-t-full" data-id="element-1120" />}
                  </>}
              </NavLink>;
        })}
        </div>
      </div>
    </nav>;
}